import { Head, router } from "@inertiajs/react"
import { useEffect, useMemo, useState } from "react"
import type { Question, Stage } from "@/types/vocational"
import { PageLayout } from "@/components/VocationalTest/PageLayout"
import { ReflectionPresenter } from "@/components/VocationalTest/ReflectionPresenter"
import { AdvancedQuestionCard } from "@/components/VocationalTest/AdvancedQuestionCard"
import type { AdvancedScores } from "@/types/advancedVocational"
import { calculateMicroProfile } from "@/data/microProfiles"

const ADVANCED_ANSWERS_KEY = "advanced_answers"
const ADVANCED_QUESTION_IDS_KEY = "advanced_question_ids"
const ADVANCED_QUESTION_OPTIONS_KEY = "advanced_question_option_values"

interface AdvancedQuestionsPageProps {
  stage?: Stage | null
  advanced_metrics?: Record<string, Array<{ metric: string; sub_metric: string; text: string }>> | null
}

export default function AdvancedQuestionsPage({ stage, advanced_metrics }: AdvancedQuestionsPageProps) {
  const [questionIndex, setQuestionIndex] = useState(0)
  const [isSubmittingResult, setIsSubmittingResult] = useState(false)
  const [answers, setAnswers] = useState<Record<number, number>>(() => {
    const raw = window.localStorage.getItem(ADVANCED_ANSWERS_KEY)
    return raw ? JSON.parse(raw) : {}
  })

  const questions = useMemo(() => stage?.questions ?? [], [stage])
  const currentQuestion: Question | undefined = questions[questionIndex]

  const totalScoring = useMemo(() => {
    return questions.filter((q) => q.options && q.options.length === 4).length
  }, [questions])

  const questionNumber = useMemo(() => {
    if (!currentQuestion) return 0
    let count = 0
    for (let i = 0; i <= questionIndex; i += 1) {
      const item = questions[i]
      if (item?.options && item.options.length === 4) count += 1
    }
    return count
  }, [currentQuestion, questionIndex, questions])

  const persistAnswers = (next: Record<number, number>) => {
    setAnswers(next)
    window.localStorage.setItem(ADVANCED_ANSWERS_KEY, JSON.stringify(next))
  }

  const computeAdvancedScores = (inputAnswers: Record<number, number>) => {
    const scores: AdvancedScores = {
      H: 0,
      E: 0,
      B: 0,
      A: 0,
      G: 0,
      C: 0,
    }

    if (!stage || !advanced_metrics) return scores

    Object.entries(inputAnswers).forEach(([questionIdStr, optionValue]) => {
      const questionId = Number(questionIdStr)
      const question = stage.questions.find((q) => q.id === questionId)
      if (!question || !question.options) return
      const metrics = advanced_metrics[String(questionId)]
      if (!metrics || metrics.length === 0) return
      const selectedOption = question.options.find((option) => option.value === optionValue)
      if (!selectedOption) return
      const metricOption = metrics.find((option) => option.text.trim() === selectedOption.text.trim())
      if (!metricOption) return
      const metricKey = metricOption.metric as keyof AdvancedScores
      if (metricKey in scores) {
        scores[metricKey] += 1
      }
    })

    return scores
  }

  const submitAdvancedResult = async (snapshotAnswers?: Record<number, number>) => {
    if (isSubmittingResult) return

    const payloadAnswers = snapshotAnswers || answers
    const scores = computeAdvancedScores(payloadAnswers)
    const microProfile = calculateMicroProfile(scores)

    setIsSubmittingResult(true)
    try {
      const response = await fetch("/advanced-test-results", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          advanced_test_result: {
            completo: true,
            pontuacoes: scores,
            respostas: payloadAnswers,
            microperfil: microProfile.name
          }
        })
      })
      if (!response.ok) {
        setIsSubmittingResult(false)
        window.alert("Não foi possível salvar o resultado avançado. Tente novamente.")
        return
      }
      router.visit("/advanced-results")
    } catch {
      setIsSubmittingResult(false)
    }
  }

  const goNext = () => {
    if (questionIndex < questions.length - 1) {
      setQuestionIndex(questionIndex + 1)
      return
    }
    submitAdvancedResult()
  }

  const handleAnswer = (answer: number) => {
    if (!currentQuestion) return

    if (currentQuestion.options && currentQuestion.options.length === 4) {
      const next = { ...answers, [currentQuestion.id]: answer }
      persistAnswers(next)
      if (questionIndex >= questions.length - 1) {
        submitAdvancedResult(next)
      }
    }

    if (currentQuestion.isReflection === true) {
      return
    }

    if (questionIndex < questions.length - 1) {
      goNext()
    }
  }

  if (!stage || questions.length === 0) {
    return (
      <PageLayout>
        <div className="text-center text-muted-foreground">Nenhuma questão avançada encontrada.</div>
      </PageLayout>
    )
  }

  if (!currentQuestion) {
    return (
      <PageLayout>
        <div className="text-center text-muted-foreground">Nenhuma questão encontrada.</div>
      </PageLayout>
    )
  }

  const isReflection = currentQuestion?.isReflection === true

  useEffect(() => {
    const scoringIds = questions.filter((q) => q.options && q.options.length === 4).map((q) => q.id)
    window.localStorage.setItem(ADVANCED_QUESTION_IDS_KEY, JSON.stringify(scoringIds))

    const optionsMap: Record<number, number[]> = {}
    questions.forEach((q) => {
      if (q.options && q.options.length === 4) {
        optionsMap[q.id] = q.options.map((opt) => opt.value)
      }
    })
    window.localStorage.setItem(ADVANCED_QUESTION_OPTIONS_KEY, JSON.stringify(optionsMap))
  }, [questions])

  return (
    <>
      <Head title="Teste avançado" />
      <PageLayout>
        <div className="w-full max-w-3xl space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-title-blue mb-2">
              {stage.title}
            </h2>
            <p className="text-[#ED7723] font-medium">{stage.description}</p>
          </div>
          {isReflection ? (
            <ReflectionPresenter question={currentQuestion} onContinue={goNext} />
          ) : (
            <AdvancedQuestionCard
              key={currentQuestion.id}
              question={currentQuestion}
              currentAnswer={answers[currentQuestion.id]}
              onAnswer={handleAnswer}
              questionNumber={Math.max(1, questionNumber)}
              totalQuestions={Math.max(1, totalScoring)}
            />
          )}
        </div>
      </PageLayout>
    </>
  )
}
